import { Component, OnInit, ViewChild } from '@angular/core';
import { Log } from 'ng2-logger';
import { ToastsManager } from 'ng2-toastr';
import { AdminService } from '../../../_services/admin.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from '../../../app.service';
import { IActionMapping, ITreeOptions, TreeComponent } from 'angular-tree-component';
import * as FileSaver from 'file-saver';
import { ACLFeature } from '../../../_models/acl-feature';
import { Permissions } from '../../../_services/permissions';

const log = Log.create('WhitelistingComponent');

@Component({
  templateUrl: 'whitelist.component.html',
  styleUrls: ['whitelist.less']
})
export class WhitelistingComponent implements OnInit {
  @ViewChild('fileInput') fileInput;

  type: string;
  id: number;
  parentId: any;
  isExporting = false;
  modulesLoaded = true;
  driversLoaded = true;
  sensorDriversLoaded = true;
  canSelectAll = false;
  canWhitelist = false;
  canWriteWhitelist = false;
  canWhitelistSensor = false;
  canWhitelistAllCompanySensor = false;

  uploadError = "";
  showUploadSuccessMessage = false;

  regions = ['EMEA', 'CN', 'ROA', 'NA', 'SA'];
  selectedRegion = null;
  selectedSensorRegion = null;

  moduleNodes = [];
  driverNodes = [];
  sensorDriverNodes = [];

  selectedDriverProductIds = [];
  selectedModuleProductIds = [];

  selectedSensorDriverProductIds = [];

  @ViewChild('modulestree')
  private modulestree: TreeComponent;

  @ViewChild('driverstree')
  private driverstree: TreeComponent;

  @ViewChild('sensordriverstree')
  private sensordriverstree: TreeComponent;

  actionMapping: IActionMapping = {
    mouse: {
      click: (tree, node) => {
        this.check(node, !node.data.checked);
        this.updateSelectedProducts();
      }
    }
  };

  treeOptions: ITreeOptions = {
    actionMapping: this.actionMapping,
  };

  constructor(private appService: AppService,
              private route: ActivatedRoute,
              private router: Router,
              private admin: AdminService,
              private permissions: Permissions,
              private toast: ToastsManager) {}

  ngOnInit() {
    log.d('ngOnInit');

    this.appService.headerData.title = 'Whitelisting';
    this.appService.headerData.path = [];

    this.route.params.subscribe(params => {
      log.data('routerparams', params);

      log.data('routersnapshot', this.route.snapshot.parent.params);

      this.type = this.route.snapshot.parent.params['type'];
      this.parentId = this.route.snapshot.parent.params['parentId'];
      this.id = params['id'];

      this.permissions.hasFeature(ACLFeature.WHITELIST_READ).subscribe((success) => {
        this.canWhitelist = success;
      });

      this.permissions.hasFeature(ACLFeature.WHITELIST_WRITE).subscribe((success) => {
        this.canWriteWhitelist = success;
      });

      this.permissions.hasFeature(ACLFeature.WRITE_SENSORS_WHITELIST).subscribe((success) => {
        this.canWhitelistSensor = success;
      });

      this.permissions.hasFeature(ACLFeature.READ_SENSORS_WHITELIST_ALL_COMPANIES).subscribe((success) => {
        this.canWhitelistAllCompanySensor = success;
      });
      this.permissions.hasFeature(ACLFeature.WHITELIST_READ_ALL).subscribe((success) => {
        this.canSelectAll = success;
        this.selectedRegion = '';
        this.selectedSensorRegion = '';
      });
    });
  }

  private fetchData() {
    this.modulesLoaded = false;
    this.driversLoaded = false;

    this.admin.getModuleTree(this.selectedRegion).subscribe((response) => {
      this.moduleNodes = response.data;
      this.modulesLoaded = true;
    });

    this.admin.getDriversTree(this.selectedRegion).subscribe((response) => {
      this.driverNodes = response.data;
      this.driversLoaded = true;
    });
  }

  public onSelectRegion(region) {
    if (this.selectedRegion === region) {
      return;
    } else {
      this.selectedRegion = region;
    }

    this.fetchData();
  }

  private fetchSensorData() {
    this.sensorDriversLoaded = false;

    this.admin.getSensorDriversTree(this.selectedSensorRegion).subscribe((response) => {
      this.sensorDriverNodes = response.data;
      this.sensorDriversLoaded = true;
    });
  }

  public onSelectSensorRegion(region) {
    if (this.selectedSensorRegion === region) {
      return;
    } else {
      this.selectedSensorRegion = region;
    }

    this.fetchSensorData();
  }

  public toggleAllChecks(type) {
    let tree, model, newCheckState;

    if (type === 'modules') {
      tree = 'modulestree';
      model = this.selectedModuleProductIds;
    } else if (type === 'drivers') {
      tree = 'driverstree';
      model = this.selectedDriverProductIds;
    } else if (type === 'sensorDrivers') {
      tree = 'sensordriverstree';
      model = this.selectedSensorDriverProductIds;
    }

    newCheckState = !model.length; // decide to either select everything or nothing

    for (const node of this[tree].treeModel.nodes) {
      this.check(this[tree].treeModel.getNodeById(node.id), newCheckState);
    }
    this.updateSelectedProducts();
  }

  public check(node, checked) {
    this.updateChildNodeCheckbox(node, checked);
    this.updateParentNodeCheckbox(node.realParent);
  }

  public updateChildNodeCheckbox(node, checked) {
    node.data.checked = checked;
    if (node.children) {
      node.children.forEach((child) => this.updateChildNodeCheckbox(child, checked));
    }
  }

  public updateParentNodeCheckbox(node) {
    if (!node) {
      return;
    }

    let allChildrenChecked = true;
    let noChildChecked = true;

    for (const child of node.children) {
      if (!child.data.checked || child.data.indeterminate) {
        allChildrenChecked = false;
      }
      if (child.data.checked) {
        noChildChecked = false;
      }
    }

    if (allChildrenChecked) {
      node.data.checked = true;
      node.data.indeterminate = false;
    } else if (noChildChecked) {
      node.data.checked = false;
      node.data.indeterminate = false;
    } else {
      node.data.checked = true;
      node.data.indeterminate = true;
    }
    this.updateParentNodeCheckbox(node.parent);
  }

  private onExport() {

    this.isExporting = true;
    this.admin.getWhitelistExport(this.selectedDriverProductIds, this.selectedModuleProductIds).subscribe((response) => {
      FileSaver.saveAs(response, `export-whitelist-${Date.now()}.xlsx`);
      this.isExporting = false;
    });
  }

  private onSensorExport(allCompanies) {
    this.isExporting = true;
    this.admin.getSensorWhitelistExport(this.selectedSensorDriverProductIds, this.selectedSensorRegion, allCompanies)
        .subscribe((response) => {
          FileSaver.saveAs(response, `export-sensor-whitelist-${Date.now()}.xlsx`);
          this.isExporting = false;
    });
  }

  updateSelectedProducts() {
    this.selectedModuleProductIds = [];
    this.selectedDriverProductIds = [];
    this.selectedSensorDriverProductIds = [];

    if (this.modulestree) {
      this.modulestree.treeModel.nodes.forEach(node => this.findSelectedProductIdsRecursive(node, this.selectedModuleProductIds));
    }
    if (this.driverstree) {
      this.driverstree.treeModel.nodes.forEach(node => this.findSelectedProductIdsRecursive(node, this.selectedDriverProductIds));
    }
    if (this.sensordriverstree) {
      this.sensordriverstree.treeModel.nodes.forEach(node => this.findSelectedProductIdsRecursive(node, this.selectedSensorDriverProductIds));
    }
  }

  findSelectedProductIdsRecursive(node, collection) {
    if (node.children && node.children.length ) {
      for (const child of node.children) {
        this.findSelectedProductIdsRecursive(child, collection);
      }
    } else {
      if (node.type === 'product' && node.checked === true) {
        collection.push(node.id);
        return node;
      }
    }
  }

  fileSubmitSensorExcel($event) {
    this.uploadError = '';
    this.showUploadSuccessMessage = false;
    const fileElement = $event.srcElement;

    // let fileElement = this.fileInput.nativeElement;
    if (fileElement.files && fileElement.files[0]) {
      const file = fileElement.files[0];

      const fileExtension = file.name.split('.').pop();
      if (fileExtension === 'xlsx') {
        this.showUploadSuccessMessage = true;
        this.admin.postSensorExcelFile(file).subscribe(() => {
          this.showUploadSuccessMessage = true;
          this.toast.success(`Whitelist import started succesfully. The import will be done in a short while, depending of the amount of combinations uploaded.`,
              null,
              {
                toastLife: 10000
              });
        }, (error) => {
          this.toast.error(`Excel imported failed`);
          this.uploadError = JSON.parse(error._body).message;
        });
      } else {
        this.uploadError = 'Please provide a valid excel file!';
      }
    } else {
      this.uploadError = 'Please provide a file!';
    }
  }

  fileSubmitExcel($event) {

    this.uploadError = '';
    this.showUploadSuccessMessage = false;
    const fileElement = $event.srcElement;

    // let fileElement = this.fileInput.nativeElement;
    if (fileElement.files && fileElement.files[0]) {
      const file = fileElement.files[0];

      const fileExtension = file.name.split('.').pop();
      if (fileExtension === 'xlsx') {
        this.showUploadSuccessMessage = true;
        this.admin.postExcelFile(file).subscribe(() => {
          this.showUploadSuccessMessage = true;
          this.toast.success(`Whitelist import started succesfully. The import will be done in a short while, depending of the amount of combinations uploaded.`,
            null,
            {
              toastLife: 10000
            });
        }, (error) => {
          this.toast.error(`Excel imported failed`);
          this.uploadError = JSON.parse(error._body).message;
        });
      } else {
        this.uploadError = 'Please provide a valid excel file!';
      }
    } else {
      this.uploadError = 'Please provide a file!';
    }
  }

  private onCancel() {
    this.navigateBack();
  }

  private onSubmit() {

  }

  private navigateBack() {

  }
}



// WEBPACK FOOTER //
// ./src/app/management/components/whitelisting/whitelist.component.ts